import type { Direction } from "./avatars.js";
import type { RoleId } from "./roles.js";
import type { ResultEntry } from "./rules/scoring.js";

/** Numeric room close/error codes (custom range, above the WebSocket range). */
export const GAME_ERROR = {
  OFFICE_FULL: 4001,
  DUPLICATE_SESSION: 4002,
  BAD_NICKNAME: 4003,
  BAD_JOIN: 4004,
  ROLE_DISABLED: 4005,
  VERSION_MISMATCH: 4006,
  /** The server couldn't load the player's profile (database trouble). */
  SERVER_BUSY: 4007,
} as const;

export type GameErrorName = keyof typeof GAME_ERROR;

export const GAME_ERROR_NAMES: Record<number, GameErrorName> = Object.fromEntries(
  Object.entries(GAME_ERROR).map(([name, code]) => [code, name as GameErrorName]),
) as Record<number, GameErrorName>;

/** Reasons an interaction or action can be rejected. */
export type InteractionErrorCode =
  | "OUT_OF_RANGE"
  | "DESK_TAKEN"
  | "BURNED_OUT"
  | "EXHAUSTED_MAX"
  | "PHASE_CELEBRATING"
  | "WRONG_ROLE"
  | "ALREADY_BUSY"
  | "NOT_IN_RELAX_ZONE"
  | "NOT_TIRED"
  | "NOT_IN_EXIT_ZONE"
  | "UNKNOWN_TARGET";

export type RadioErrorCode = "NOT_IN_RADIO_AREA" | "RADIO_COOLDOWN" | "STATION_UNAVAILABLE";

export type ActionErrorCode = InteractionErrorCode | RadioErrorCode | "NOT_WORKING" | "EXIT_DENIED";

/** Friendly copy for every reject code, used by the client toasts. */
export const ERROR_MESSAGES: Record<ActionErrorCode, string> = {
  OUT_OF_RANGE: "You're too far away.",
  DESK_TAKEN: "That desk was just taken.",
  BURNED_OUT: "Too exhausted — grab a coffee ☕",
  EXHAUSTED_MAX: "You're running on fumes. Take a break.",
  PHASE_CELEBRATING: "Project shipped! Wait for the next one.",
  WRONG_ROLE: "This workstation isn't for your role.",
  ALREADY_BUSY: "You're already busy.",
  NOT_IN_RELAX_ZONE: "No coffee here — head to the relax area.",
  NOT_TIRED: "You're fully rested.",
  NOT_IN_EXIT_ZONE: "Stand at the door to leave.",
  UNKNOWN_TARGET: "Nothing to interact with here.",
  NOT_WORKING: "You're not working.",
  EXIT_DENIED: "You can't leave right now.",
  NOT_IN_RADIO_AREA: "Walk up to the hi-fi first.",
  RADIO_COOLDOWN: "Easy, DJ — wait a moment.",
  STATION_UNAVAILABLE: "That station can't be played right now.",
};

export type ActivityName = "idle" | "working" | "coffee";
export type ProjectPhase = "active" | "celebrating";

export const EMOTES = {
  coffee: "☕",
  thumbsup: "👍",
  fire: "🔥",
  sleepy: "😴",
  party: "🎉",
  idea: "💡",
} as const;

export type EmoteId = keyof typeof EMOTES;
export const EMOTE_IDS = Object.keys(EMOTES) as EmoteId[];
export const EMOTE_BY_KEY: Record<string, EmoteId> = {
  "1": "coffee",
  "2": "thumbsup",
  "3": "fire",
  "4": "sleepy",
  "5": "party",
  "6": "idea",
};

export function isEmoteId(value: unknown): value is EmoteId {
  return typeof value === "string" && value in EMOTES;
}

/** Options a client passes when joining the office room. */
export interface JoinOptions {
  token: string;
  nickname: string;
  avatarId: string;
  role: RoleId;
  clientVersion: string;
}

/* ------------------------------------------------------------------ */
/* Client → Server                                                     */
/* ------------------------------------------------------------------ */

export interface MoveMessage {
  x: number;
  y: number;
  dir: Direction;
  moving: boolean;
  seq: number;
}

export interface InteractMessage {
  targetId?: string;
}

export interface ChatSendMessage {
  text: string;
}

export interface EmoteMessage {
  emote: EmoteId;
}

export interface RadioTuneMessage {
  /** A Radio Browser `stationuuid`; the server resolves the stream itself. */
  stationId: string;
}

export type ClientMessageName =
  | "hello"
  | "move"
  | "interact"
  | "stopActivity"
  | "chat"
  | "emote"
  | "exit"
  | "requestLeaderboard"
  | "radioTune"
  | "radioStop";

export interface ClientMessages {
  hello: Record<string, never>;
  move: MoveMessage;
  interact: InteractMessage;
  stopActivity: Record<string, never>;
  chat: ChatSendMessage;
  emote: EmoteMessage;
  exit: Record<string, never>;
  requestLeaderboard: Record<string, never>;
  radioTune: RadioTuneMessage;
  radioStop: Record<string, never>;
}

/* ------------------------------------------------------------------ */
/* Server → Client                                                     */
/* ------------------------------------------------------------------ */

export interface ChatMsg {
  id: string;
  kind: "player" | "system";
  sessionId?: string;
  nickname?: string;
  text: string;
  ts: number;
}

export interface Results {
  projectId: number;
  projectName: string;
  durationMs: number;
  entries: ResultEntry[];
  nextStartsAt: number;
}

export interface LeaderboardRow {
  playerId: string;
  nickname: string;
  avatarId: string;
  totalScore: number;
  projectsContributed: number;
}

export interface WelcomeMessage {
  sessionId: string;
  token: string;
  nickname: string;
  role: RoleId;
  serverTime: number;
  timeScale: number;
  chatHistory: ChatMsg[];
  lastResults?: Results;
}

export interface EmoteBroadcast {
  sessionId: string;
  emote: EmoteId;
  until: number;
}

export interface PositionCorrection {
  x: number;
  y: number;
  seq: number;
}

export interface ActionRejected {
  action: string;
  code: ActionErrorCode;
  message: string;
}

export type PlayerEventName = "burnout" | "rested" | "coffee_done";

export interface PlayerEvent {
  sessionId: string;
  event: PlayerEventName;
}

export interface ProjectCompletedMessage extends Results {}

export interface ProjectStartedMessage {
  projectId: number;
  projectName: string;
  requiredUnits: number;
}

export interface LeaderboardMessage {
  allTime: LeaderboardRow[];
  lastResults?: Results;
}

export type ServerMessageName =
  | "welcome"
  | "chat"
  | "emote"
  | "positionCorrection"
  | "actionRejected"
  | "playerEvent"
  | "projectCompleted"
  | "projectStarted"
  | "leaderboard";

export interface ServerMessages {
  welcome: WelcomeMessage;
  chat: ChatMsg;
  emote: EmoteBroadcast;
  positionCorrection: PositionCorrection;
  actionRejected: ActionRejected;
  playerEvent: PlayerEvent;
  projectCompleted: ProjectCompletedMessage;
  projectStarted: ProjectStartedMessage;
  leaderboard: LeaderboardMessage;
}
